import {useFormik} from 'formik'
import React, {useCallback, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import SettingsService from '../../../../core/_requests'
import {Button, ButtonBack} from '../../../../../../../_gori/partials/widgets'
import {toast} from 'react-toastify'
import useCancelToken from '../../../../../../../_gori/hooks/UseCancelToken'
import * as Yup from 'yup'
import {OrdersConfig, OrderType} from '../../../../../orders/core/_const'
import {StoresListing} from '../StoresListing'
import {isEmpty} from 'lodash'
import {PromotionBanner, BannerContent} from '../../../../../common/components/PromotionBanner'
import {usePromoEligibility} from '../../../../../../../_gori/hooks/UsePromoEligibility'
import {ConnectionLink} from '../ConnectionLink'
import {KTSVG, StorageHelpers} from '../../../../../../../_gori/helpers'
import {useNavigate} from 'react-router-dom'
import UseYupValidate from '../../../../../../../_gori/hooks/UseYupValidate'
import {FromOrToModal} from '../../../../../orders'

export const EBAY_SHIP_FROM_STORAGE_KEY = 'ebay_ship_from'

const ConnectEbay: React.FC = () => {
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {getMarketPromoStatus} = usePromoEligibility()
  const ebayPromoStatus = getMarketPromoStatus('ebay')
  const isPromoEligible = ebayPromoStatus.isEligible
  const isPromoApplied = ebayPromoStatus.isApplied
  const {newCancelToken} = useCancelToken()
  const navigate = useNavigate()
  const {stringYup, orderYup, regexYup, infoYup} = UseYupValidate()
  const [showModal, setShowModal] = useState<{shippingAddress: boolean}>({
    shippingAddress: false,
  })

  const validationSchema = Yup.object().shape({})

  const buildFromData = useCallback((shippingAddress: any) => {
    // Convert flat shipping address {company, first_name, street1, ...}
    // to modal format {from_company, from_first_name, from_street1, ...}
    const fromData: any = {}
    Object.entries(OrdersConfig.SHIPPING).forEach(([, value]) => {
      switch (value.value) {
        case OrdersConfig.SHIPPING.RESIDENTIAL.value:
          fromData[`from_${value.value}`] = shippingAddress?.is_residential ?? false
          break
        case OrdersConfig.SHIPPING.VERIFIED.value:
        case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
          break
        default:
          fromData[`from_${value.value}`] = shippingAddress?.[value.value] ?? ''
          break
      }
    })
    return fromData
  }, [])

  const initialValues = useMemo(() => {
    const init: any = Object.entries(OrdersConfig.SHIPPING).reduce(
      (initValue, [, value]) => {
        switch (value.value) {
          case OrdersConfig.SHIPPING.RESIDENTIAL.value:
            initValue.from[`from_${value.value}`] = false
            break
          case OrdersConfig.SHIPPING.VERIFIED.value:
          case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
            break
          default:
            initValue.from[`from_${value.value}`] = ''
            break
        }
        return initValue
      },
      {from: {}}
    )
    return {
      ...init,
      store_type: OrderType.EBAY,
    }
  }, [])

  const transformModalData = useCallback((modalValues: any) => {
    // Modal returns {from_company, from_first_name, from_street1, ...}
    // Backend expects {company, first_name, street1, ...}
    const transformed: any = {}
    Object.keys(modalValues).forEach((key) => {
      const newKey = key.startsWith('from_') ? key.substring(5) : key
      transformed[newKey] = modalValues[key]
    })
    return transformed
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async () => {
      setIsLoading(true)
      const config = {cancelToken: newCancelToken()}
      await SettingsService.getEbayConsentLink(config)
        .then((res) => {
          setIsLoading(false)
          window.location.replace(res)
        })
        .catch((error) => {
          setIsLoading(false)
          let message = error?.response?.data?.error?.message ?? ''
          toast.error(isEmpty(message) ? intl.formatMessage({id: 'CONNECTION_FAILED'}) : message)
        })
    },
  })

  const validateAddress = useCallback(
    (name) => {
      return {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  const ebayBannerContent: Partial<BannerContent> = {
    helpLinkTo: '/help-center/store-connection/ebay',
  }

  const handleReconnect = useCallback(
    (storeData: any) => {
      // If store has shipping_address, pre-populate formik from field
      if (storeData?.shipping_address) {
        const fromData = buildFromData(storeData.shipping_address)
        formik.setFieldValue('from', fromData)
      }
      setShowModal((prev) => ({...prev, shippingAddress: true}))
    },
    [buildFromData, formik]
  )

  return (
    <>
      {showModal.shippingAddress && (
        <FromOrToModal
          data={{
            show: showModal.shippingAddress,
            name: 'from',
            data: formik.getFieldProps('from').value,
            validate: validateAddress('from'),
          }}
          labelModal={
            intl.formatMessage({id: 'EBAY'}) + ' ' + intl.formatMessage({id: 'SHIP_FROM_LOCATION'})
          }
          handleSave={(name, data) => {
            const transformedData = transformModalData(data)
            StorageHelpers.setItemLocalStorage(EBAY_SHIP_FROM_STORAGE_KEY, transformedData)
            setShowModal((prev) => ({...prev, shippingAddress: false}))
            formik.submitForm()
          }}
          handleClose={() => {
            setShowModal((prev) => ({...prev, shippingAddress: false}))
          }}
        />
      )}
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='store-connect-page'>
          <div className='store-connect-page__header'>
            <div className='store-connect-page__header-main'>
              <div className='d-flex align-items-center store-connect-page__back'>
                <ButtonBack className='store-connect-page__back-button me-0' />
                <button
                  type='button'
                  className='store-connect-page__back-arrow'
                  aria-label='Go back'
                  onClick={() => navigate(-1)}
                >
                  <KTSVG path='/media/gori/left_arrow.svg' />
                </button>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex gap-4 align-items-center flex-wrap'>
                  <span className='store-connect-page__title fw-bolder text-dark fs-3'>
                    {intl.formatMessage({id: 'CONNECT_TO'})} eBay
                  </span>
                </div>
                <ConnectionLink url={ebayBannerContent.helpLinkTo || ''} />
              </div>
            </div>
            {isPromoApplied && (
              <PromotionBanner
                isEligible={isPromoEligible}
                isApplied={isPromoApplied}
                content={ebayBannerContent}
              />
            )}
          </div>
          <div className='store-connect-page__body'>
            <div className='store-connect-page__form-card store-connect-page__form-card--stacked'>
              <div className='store-connect-page__form-connect-bar'>
                <p className='store-connect-page__form-connect-notice'>
                  {intl.formatMessage({id: 'STORE_CONNECTION_MARKETPLACE_REDIRECT_NOTICE'})}
                </p>
                <Button
                  className='btn btn-advanced-primary store-connect-page__form-stack-connect'
                  loading={isLoading}
                  loadingText={intl.formatMessage({id: 'CONNECT'})}
                  label={intl.formatMessage({id: 'CONNECT'})}
                  event={() => {
                    setShowModal((prev) => ({...prev, shippingAddress: true}))
                  }}
                />
              </div>
            </div>
            <div className='store-connect-page__stores'>
              <StoresListing storeType={OrderType.EBAY} onReconnect={handleReconnect} />
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {ConnectEbay}
